import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Props { ownerId: string; }
interface Spot { id: string; address: string; price_per_hour: number; is_available: boolean; }

const SpotsManagerTab = ({ ownerId }: Props) => {
  const { toast } = useToast();
  const [spots, setSpots] = useState<Spot[]>([]);
  const [loading, setLoading] = useState(true);
  const [bulkPrice, setBulkPrice] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = async () => {
    const { data } = await supabase
      .from("parking_spots")
      .select("id, address, price_per_hour, is_available")
      .eq("owner_id", ownerId);
    setSpots(data ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [ownerId]);

  const toggleAvail = async (s: Spot) => {
    const { error } = await supabase
      .from("parking_spots")
      .update({ is_available: !s.is_available })
      .eq("id", s.id);
    if (error) return toast({ title: "Failed", variant: "destructive" });
    setSpots((prev) => prev.map((x) => (x.id === s.id ? { ...x, is_available: !s.is_available } : x)));
  };

  const applyBulk = async () => {
    const price = parseFloat(bulkPrice);
    if (!price || selected.size === 0) {
      toast({ title: "Select spots and enter price", variant: "destructive" });
      return;
    }
    const { error } = await supabase
      .from("parking_spots")
      .update({ price_per_hour: price })
      .in("id", Array.from(selected));
    if (error) return toast({ title: "Failed", description: error.message, variant: "destructive" });
    toast({ title: `Updated ${selected.size} spots to ₹${price}/hr` });
    setSelected(new Set());
    setBulkPrice("");
    load();
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Bulk price update</CardTitle>
          <CardDescription>Select spots below, set new ₹/hr, then apply</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <Input
              type="number"
              placeholder="New price per hour"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
            />
          </div>
          <Button onClick={applyBulk}>Apply to {selected.size} selected</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pause / Resume listings</CardTitle>
          <CardDescription>Quickly toggle availability per spot</CardDescription>
        </CardHeader>
        <CardContent>
          {spots.length === 0 ? (
            <p className="text-sm text-muted-foreground">No spots yet.</p>
          ) : (
            <ul className="divide-y">
              {spots.map((s) => (
                <li key={s.id} className="py-3 flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggleSelect(s.id)}
                    className="h-4 w-4"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{s.address}</p>
                    <p className="text-sm text-muted-foreground">₹{s.price_per_hour}/hr</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {s.is_available ? "Active" : "Paused"}
                    </span>
                    <Switch checked={s.is_available} onCheckedChange={() => toggleAvail(s)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SpotsManagerTab;
